import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebase-config';
import { doc, getDoc } from 'firebase/firestore';
import SignFooter from '../components/FlowComponents/SignFooter/SignFooter';
import SignHeader from '../components/FlowComponents/SignHeader/SignHeader';

function LmsCourseDetails() {
    const navigate = useNavigate();
    const { packageId, batchId, courseId } = useParams();
    const [course, setCourse] = useState(null);
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!courseId) {
            setError('No course ID provided');
            setLoading(false);
            return;
        }

        const fetchCourseAndModules = async () => {
            try {
                // Step 1: Fetch course by ID
                const courseDocRef = doc(db, 'lms_course', courseId);
                const courseDocSnap = await getDoc(courseDocRef);

                if (!courseDocSnap.exists()) {
                    setError('Course not found');
                    setLoading(false);
                    return;
                }

                const courseData = courseDocSnap.data();
                setCourse({ id: courseId, ...courseData });

                // Step 2: Fetch modules if available
                if (courseData.courseContainingModules?.length > 0) {
                    const modulePromises = courseData.courseContainingModules.map(async (moduleId) => {
                        const moduleDocRef = doc(db, 'lms_module', moduleId);
                        const moduleDocSnap = await getDoc(moduleDocRef);
                        return moduleDocSnap.exists() ? { id: moduleId, ...moduleDocSnap.data() } : null;
                    });
                    const moduleData = await Promise.all(modulePromises);
                    setModules(moduleData.filter(module => module)); // Filter out null values
                }

                setLoading(false);
            } catch (err) {
                console.error('Failed to fetch course or modules:', err);
                setError('Failed to fetch course or modules');
                setLoading(false);
            }
        };

        fetchCourseAndModules();
    }, [courseId]);


    const handleModuleClick = (moduleId) => {
        navigate(`/package/${packageId}/${batchId}/${courseId}/${moduleId}`);
    };

    if (loading) {
        return (
            <>

                <div className="bg-black min-h-screen text-white font-poppins p-6 flex flex-col items-center">
                    <p>Loading...</p>
                </div>

            </>
        );
    }

    if (error || !course) {
        return (
            <>

                <div className="bg-black min-h-screen text-white font-poppins p-6 flex flex-col items-center">
                    <p>{error || 'Course not found'}</p>
                </div>

            </>
        );
    }

    return (
        <>

            <div className="bg-black min-h-screen text-white font-poppins p-6 flex flex-col items-center">
                <div className="max-w-4xl w-full">

                    <div className='text-2xl text-yellow-600 mb-4'>Course Page</div>
                    {/* Course Details */}
                    <h1 className="text-[1.75rem] font-medium leading-9 bg-custom-gradient bg-clip-text text-transparent mb-4">
                        {course.courseName || `Course ${course.id}`}
                    </h1>


                    <div className='flex gap-4 border border-gray-800 rounded-lg'>

                        <div className="w-1/2 h-auto  overflow-hidden p-6">
                            <img
                                src={course.courseBanner}
                                alt={course.courseName}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="p-6 w-1/2">
                            <div
                                className="text-xl font-normal text-white leading-9 mb-4"
                                dangerouslySetInnerHTML={{ __html: course.courseDescription }}
                            />
                            <hr className="w-full border-t border-[rgba(231,233,235,0.3)] mb-4" />
                            <ul className="flex flex-col gap-2 list-none">
                                <li className="flex items-center gap-2 text-[1.2rem] font-normal text-white leading-[2.125rem]">
                                    <span className="w-2 h-2 rounded-full bg-custom-gradient"></span>
                                    Price: ₹{parseFloat(course.coursePrice).toLocaleString('en-IN')}
                                </li>
                                <li className="flex items-center gap-2 text-[1.2rem] font-normal text-white leading-[2.125rem]">
                                    <span className="w-2 h-2 rounded-full bg-custom-gradient"></span>
                                    Created: {new Date(course.createdAt).toLocaleDateString()}
                                </li>
                                <li className="flex items-center gap-2 text-[1.2rem] font-normal text-white leading-[2.125rem]">
                                    <span className="w-2 h-2 rounded-full bg-custom-gradient"></span>
                                    Updated: {new Date(course.updatedAt).toLocaleDateString()}
                                </li>
                            </ul>
                        </div>

                    </div>

                    {/* Modules List */}
                    <h2 className="text-[1.75rem] mt-10 font-medium leading-9 bg-custom-gradient bg-clip-text text-transparent mb-4">
                        Included Modules
                    </h2>
                    <div className="flex flex-col gap-4">
                        {modules.length > 0 ? (
                            modules.map((module) => (
                                <div
                                    onClick={() => handleModuleClick(module.id)}
                                    key={module.id}
                                    className="bg-card-bg rounded-xl p-5 flex gap-6 cursor-pointer"
                                >
                                    <img
                                        src={module.moduleBanner}
                                        alt={module.moduleName}
                                        className="h-full w-24 object-cover rounded-lg"
                                    />

                                    <div className='flex flex-col gap-2'>
                                        <h3 className="text-[1.5rem] font-medium text-white leading-9">
                                            {module.moduleName || `Module ${module.id}`}
                                        </h3>
                                        {module.moduleDescription && (
                                            <div
                                                className="text-xl font-normal text-white leading-9 mb-4"
                                                dangerouslySetInnerHTML={{ __html: module.moduleDescription }}
                                            />
                                        )}
                                        {module.duration && (
                                            <p className="text-[1.2rem] font-normal text-white leading-[2.125rem]">
                                                Duration: {module.duration}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-[1.2rem] font-normal text-white leading-[2.125rem]">
                                No modules found for this course.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default LmsCourseDetails;